// frontend/src/components/HeroDemo.jsx
// Animated illustration of the save → sync → resume flow shown beneath the
// hero on Landing.js. Pure CSS keyframes via MUI's sx prop — no external
// animation library, no real screen recording. The point is to make the
// "Save your research session. Resume it anywhere." headline visually obvious
// in 4 seconds without making the user read the bullet list below it.
//
// Layout: BrowserWindow on the left, a horizontal arrow track in the middle
// with a glowing session card that travels left-to-right on a 4s loop, and a
// PhoneFrame on the right that "receives" the card. The phone's tab list
// fades in as the card lands.
//
// Reduced-motion users see a static three-step illustration (no animation).

import React from 'react';
import { Box, Typography, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// --- Fake tab list (illustrative only) ---------------------------------------

const FAKE_TABS = [
  { color: '#10A37F', label: 'Climate research findings.pdf' },
  { color: '#4285F4', label: 'Polar ice melt 2024 — WSJ' },
  { color: '#D4A574', label: 'EU emissions targets' },
  { color: '#FF6B6B', label: 'Renewable energy deal' },
  { color: '#6366F1', label: 'Carbon offsets primer' },
  { color: '#999',    label: '+3 more tabs' },
];

// --- Browser window mockup ---------------------------------------------------

const BrowserWindow = () => {
  const theme = useTheme();
  return (
    <Box
      role="img"
      aria-label="Desktop browser with multiple research tabs open"
      sx={{
        width: { xs: 260, sm: 320, md: 380 },
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 4,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Title bar with traffic-light dots */}
      <Box
        sx={{
          height: 28,
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          gap: 0.75,
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FF5F57' }} />
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FEBC2E' }} />
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#28C840' }} />
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            fontSize: '0.6rem',
            color: 'text.secondary',
            fontFamily: 'monospace',
          }}
        >
          chrome.research
        </Box>
      </Box>

      {/* Fake tab list */}
      <Box sx={{ p: 1.5 }}>
        <Stack spacing={0.75}>
          {FAKE_TABS.map((tab, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: tab.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                noWrap
                sx={{ flex: 1, fontSize: '0.7rem', color: 'text.primary' }}
              >
                {tab.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

// --- Phone mockup ------------------------------------------------------------

const PhoneFrame = ({ animated }) => {
  const theme = useTheme();
  return (
    <Box
      role="img"
      aria-label="Phone showing the same session restored from the cloud"
      sx={{
        width: { xs: 130, sm: 150, md: 170 },
        height: { xs: 260, sm: 300, md: 340 },
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: 4,
        border: `2px solid ${theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300'}`,
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
      }}
    >
      {/* Notch */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 60,
          height: 18,
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          zIndex: 1,
        }}
      />

      {/* Tab list — fades in via animation */}
      <Box sx={{ p: 1.5, pt: 4 }}>
        <Stack spacing={0.5}>
          {FAKE_TABS.map((tab, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 0.75,
                py: 0.4,
                borderRadius: 0.75,
                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                // Tabs must never fully disappear — an empty phone frame reads
                // as a broken image, and reduced-motion users would otherwise
                // see a blank slab permanently.
                opacity: animated ? 0.45 : 1,
                ...(animated && {
                  animation: `phoneTabFadeIn 4s ease-in-out ${2.5 + i * 0.12}s infinite`,
                }),
                '@keyframes phoneTabFadeIn': {
                  '0%, 50%': { opacity: 0.45, transform: 'translateX(4px)' },
                  '60%, 90%': { opacity: 1, transform: 'translateX(0)' },
                  '100%': { opacity: 0.45, transform: 'translateX(0)' },
                },
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: tab.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                noWrap
                sx={{ flex: 1, fontSize: '0.55rem', color: 'text.primary' }}
              >
                {tab.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

// --- The animated session card that travels between devices -----------------

const FlyingCard = () => {
  const theme = useTheme();
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: '50%',
        left: '0%',
        transform: 'translate(-50%, -50%)',
        width: 56,
        height: 36,
        borderRadius: 1,
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark || theme.palette.primary.main})`,
        boxShadow: `0 0 20px ${theme.palette.primary.main}`,
        animation: 'flyingCard 4s ease-in-out infinite',
        '@keyframes flyingCard': {
          '0%, 10%': { left: '0%', opacity: 0, transform: 'translate(-50%, -50%) scale(0.6)' },
          '20%': { left: '0%', opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
          '60%': { left: '100%', opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
          '70%': { left: '100%', opacity: 0, transform: 'translate(-50%, -50%) scale(0.6)' },
          '100%': { left: '100%', opacity: 0, transform: 'translate(-50%, -50%) scale(0.6)' },
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: '0.55rem',
        fontWeight: 700,
        letterSpacing: 0.5,
      }}
    >
      SESSION
    </Box>
  );
};

// --- Main exported component ------------------------------------------------

const HeroDemo = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const animated = !prefersReducedMotion;

  return (
    <Box
      sx={{
        mt: 6,
        mb: 4,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 3, sm: 1, md: 2 },
        maxWidth: 900,
        mx: 'auto',
      }}
    >
      <BrowserWindow />

      {/* Connector — horizontal track with the flying card */}
      <Box
        sx={{
          flex: { xs: 'none', sm: 1 },
          minWidth: { sm: 80, md: 120 },
          height: { xs: 60, sm: 60 },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // On mobile, the layout is vertical so rotate the connector
          transform: { xs: 'rotate(90deg)', sm: 'none' },
        }}
      >
        {/* The track itself — a thin dashed line */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: 2,
            transform: 'translateY(-50%)',
            background: `repeating-linear-gradient(to right, ${theme.palette.divider} 0 8px, transparent 8px 14px)`,
          }}
        />

        {/* Labels above and below the track */}
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            left: '50%',
            transform: 'translate(-50%, -100%)',
            px: 1,
            py: 0.25,
            borderRadius: 0.75,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontSize: '0.6rem',
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          ☁ Sync
        </Box>

        {animated && <FlyingCard />}

        {!animated && (
          // Reduced-motion fallback: a static arrow
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '1.2rem',
              color: 'primary.main',
            }}
          >
            →
          </Box>
        )}
      </Box>

      <PhoneFrame animated={animated} />
    </Box>
  );
};

export default HeroDemo;
